import type { MedicareCertPrefillData } from "@services/facilityManagement";

/**
 * How the server satisfied a Medicare cert prefill / manual refresh.
 * The product goal is **CMS Medicare provider data**; this tracks whether a **live**
 * CMS HTTP call happened, a **cached** CMS result was reused, or **CMS was skipped**
 * (facility record only).
 */
export type MedicareRefreshProvenance =
  | "cms_live"
  | "cms_redis_only"
  | "cms_mixed_cache_ccn_fallback_name"
  | "facility_record_no_cms_call"
  | "unknown";

export function getMedicareRefreshProvenance(
  diag: Record<string, unknown> | null | undefined,
): MedicareRefreshProvenance {
  const d = diag && typeof diag === "object" ? diag : {};
  const keys = Object.keys(d).filter((k) => {
    const v = (d as Record<string, unknown>)[k];
    return v !== undefined && v !== null && v !== "";
  });
  if (keys.length === 0) {
    return "facility_record_no_cms_call";
  }

  const msg = typeof d.message === "string" ? d.message : "";
  const hasSecondary = d.secondary_query_mode === "name_state";
  const reqUrl =
    typeof d.request_url === "string" && d.request_url.trim().length > 0;

  if (msg === "cache_hit" && hasSecondary) {
    return "cms_mixed_cache_ccn_fallback_name";
  }
  if (msg === "cache_hit") {
    return "cms_redis_only";
  }
  if (reqUrl) {
    return "cms_live";
  }
  return "unknown";
}

/**
 * Plain-language explanation after a successful Medicare cert prefill / refresh.
 * Frames everything as **CMS-published provider data** when the server used CMS
 * (live or cache); clearly separates when **no CMS call** ran (saved facility date).
 */
export function buildMedicareManualRefreshUserSummary(
  data: MedicareCertPrefillData,
  diag: Record<string, unknown> | null | undefined,
): string {
  const hasOriginal = Boolean(data.original_certification_date);
  const hasNext = Boolean(data.next_certification_date);
  const hasAnyDate = hasOriginal || hasNext;
  const provenance = getMedicareRefreshProvenance(diag);
  const lead =
    diag && typeof diag === "object" && (diag as Record<string, unknown>).force_refresh === true
      ? "Forced CMS refresh: the server ignored the “fresh certification date” guard and skipped Redis read-through cache so Medicare HTTP could run again. "
      : "";

  if (
    data.certification_date_conflict &&
    data.profile_certification_date &&
    data.cms_pos_certification_date
  ) {
    return (
      lead +
      `Conflict: your saved Medicare certification date (${data.profile_certification_date}) ` +
      `does not match CMS Provider of Services (${data.cms_pos_certification_date}). ` +
      "The form kept your saved date. Use “Use CMS date” if the CMS POS value is correct."
    );
  }

  if (provenance === "facility_record_no_cms_call") {
    if (hasAnyDate) {
      return (
        lead +
        "No new CMS lookup ran: your saved Medicare certification date on this facility is recent enough that we skip calling CMS again. " +
        "The dates on this form reflect that saved facility record (and the next revalidation date was recalculated from it). " +
        "To change what is stored, edit the certification date or ask an administrator."
      );
    }
    return (
      lead +
      "No CMS lookup ran (your saved certification date did not require a refresh), and CMS did not return new dates on this request. " +
      "If you expected a live CMS pull, check the CCN on your profile or contact support."
    );
  }

  if (provenance === "cms_redis_only") {
    if (!hasAnyDate) {
      return (
        lead +
        "We did not place a new call to CMS this time. The server reused a cached copy of CMS Medicare provider data for your CCN, and that cache entry did not include a usable certification date to show."
      );
    }
    return (
      lead +
      "Source: CMS (Medicare Provider of Services). This click did not open a new live request to CMS; we reused our server cache of an earlier CMS lookup for your provider number. " +
      "The certification and revalidation dates on this form are from that CMS-published data—delivered from cache so it is still CMS, not your database inventing dates."
    );
  }

  if (provenance === "cms_mixed_cache_ccn_fallback_name") {
    const p = diag?.secondary_query_params as Record<string, unknown> | undefined;
    const name =
      typeof p?.FAC_NAME === "string" && p.FAC_NAME.trim()
        ? p.FAC_NAME.trim()
        : "this facility’s name";
    const st =
      typeof p?.STATE_CD === "string" && p.STATE_CD.trim()
        ? p.STATE_CD.trim().toUpperCase()
        : "";
    const ccnLabel =
      diag?.ccn_source === "profile_ccn"
        ? "the CCN on this profile"
        : diag?.ccn_source === "facility_unique_id"
          ? "the facility ID on file"
          : "your CCN / provider number";

    const ccnPart =
      diag?.message === "cache_hit"
        ? `Using ${ccnLabel}, we reused a cached CMS lookup that had no usable certification date, then matched CMS provider data by name (“${name}”)${st ? ` in ${st}` : ""}.`
        : `Using ${ccnLabel}, CMS did not return a certification date, then we matched CMS provider data by name (“${name}”)${st ? ` in ${st}` : ""}.`;

    if (!hasAnyDate) {
      return `${lead}${ccnPart} No certification date was available to fill in.`;
    }
    return `${lead}${ccnPart} All filled dates are from CMS’s Medicare provider files.`;
  }

  if (provenance === "cms_live") {
    const detail =
      typeof diag?.message === "string" && diag.message.trim() && diag.message !== "cache_hit"
        ? ` ${diag.message}`
        : "";
    if (!hasAnyDate) {
      return (
        lead +
        "We contacted CMS’s Medicare Provider of Services over the network, but no usable certification date was returned for this facility." +
        (detail ? ` (${detail.trim()})` : "")
      );
    }
    return (
      lead +
      "We just pulled CMS Medicare Provider of Services data over the network and filled the form from that live CMS response." +
      (detail ? ` ${detail.trim()}` : "")
    );
  }

  // unknown — still emphasize CMS when we have a diagnostic message
  const fallbackMsg =
    typeof diag?.message === "string" && diag.message.trim()
      ? String(diag.message)
      : "";
  if (hasAnyDate) {
    return (
      lead +
      (fallbackMsg ? `${fallbackMsg} ` : "") +
      "The dates on this form were updated using CMS-related lookup results from the server."
    );
  }
  return (
    lead +
    (fallbackMsg ? `${fallbackMsg} ` : "") +
    "CMS did not return certification dates for this facility. Double-check the CCN on your profile, then try again or contact support if it still fails."
  );
}

/** Short title for the success alert (non-admin). */
export function medicareManualRefreshSuccessTitle(
  hasAnyDate: boolean,
  diag: Record<string, unknown> | null | undefined,
  options?: { certificationDateConflict?: boolean },
): string {
  if (options?.certificationDateConflict) {
    return "Medicare date conflict with CMS";
  }
  const forced =
    diag && typeof diag === "object" && (diag as Record<string, unknown>).force_refresh === true;
  if (forced) {
    return hasAnyDate ? "Medicare dates from CMS (forced refresh)" : "Forced CMS refresh — no dates";
  }
  const p = getMedicareRefreshProvenance(diag);
  if (!hasAnyDate) {
    return p === "facility_record_no_cms_call"
      ? "No new CMS data for this request"
      : "CMS lookup finished — no dates to show";
  }
  switch (p) {
    case "facility_record_no_cms_call":
      return "Using saved dates — CMS not queried";
    case "cms_redis_only":
      return "Medicare dates from CMS (cached)";
    case "cms_mixed_cache_ccn_fallback_name":
      return "Medicare dates from CMS (name & state)";
    case "cms_live":
      return "Medicare dates from CMS (live)";
    default:
      return "Medicare dates updated";
  }
}
